// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { UpdateShipmentBody } from '@evolvemep/api-ts';

import { useShipments } from 'modules/Shop/Shipments/ShipmentsPage/useShipments';

import type { ShipmentDetailPageProps } from './ShipmentDetailWrapper';
import { useCanEditShipment } from './useCanEditShipment';

/**
 * Wrapper around `useShipments` and `useCanEditShipment`
 * Wraps the `updateShipment` method from `useShipments`, providing the shipment ID and automatically triggering the `onShipmentUpdated` callback after a successful update.
 * Wraps the `canEditShipment` method from `useCanEditShipment`, adding a check that there is no ongoing mutation.
 **/
export const useUpdateShipment = ({ shipment, onShipmentUpdated }: ShipmentDetailPageProps) => {
  const { canEditShipment: baseCanEditShipment } = useCanEditShipment(shipment);

  const { updateShipment: updateShipmentApiCall, mutationRunning } = useShipments();

  const updateShipment = useCallback(
    async (body: UpdateShipmentBody) => updateShipmentApiCall(shipment.shipmentId, body).then(onShipmentUpdated),
    [shipment.shipmentId, updateShipmentApiCall, onShipmentUpdated],
  );

  const canEditShipment = (permission: Parameters<typeof baseCanEditShipment>[0]) =>
    baseCanEditShipment(permission) && !mutationRunning;

  return {
    updateShipment,
    canEditShipment,
    mutationRunning,
  };
};
